<script setup>
import {computed, ref, watch} from 'vue'
import {useI18n} from "vue-i18n";

// components
import ButtonLoading from "@/components/Recurring/ButtonLoading.vue";

// the applied value goes out with the event, '' when the search is reset
const emit = defineEmits(["submit-search"])
const props = defineProps({
    loading: {
        type: Boolean,
        default: false
    },
    minLength: {
        type: [String, Number],
        default: 3
    },
    maxLength: {
        type: [String, Number],
        default: null
    },
    // phone numbers are searched by digits only
    numericOnly: {
        type: Boolean,
        default: false
    },
    // the search the parent has applied, for parents that keep it themselves;
    // left out, the component remembers its own
    applied: {
        type: String,
        default: undefined
    }
})

const {t: $t} = useI18n()
const search = defineModel({default: ''})
const ownApplied = ref('')

const appliedValue = computed(() => props.applied ?? ownApplied.value)

// the cross resets: shown for the applied text and for an emptied field,
// which would otherwise leave the list filtered with nothing to undo it
const isClear = computed(() => !!appliedValue.value && (!search.value || search.value === appliedValue.value))

const isButtonDisabled = computed(() => !isClear.value && search.value.length < Number(props.minLength))

const submit = (value) => {
    search.value = value
    ownApplied.value = value
    emit('submit-search', value)
}

const applySearch = () => {
    if (props.loading) return

    if (isClear.value) {
        submit('')
    } else if (!isButtonDisabled.value) {
        submit(search.value)
    }
}

const onEscape = () => {
    if (isClear.value) {
        submit('')
    } else {
        search.value = ''
    }
}

// the parent may swap the applied search under the field (tiktok keeps one per
// visibility tab), so the text follows it
watch(() => props.applied, (value) => {
    if (value !== undefined) {
        search.value = value
    }
})

watch(search, (value) => {
    if (props.numericOnly && /\D/.test(value)) {
        search.value = value.replace(/\D/g, '')
        return
    }

    // erasing the text is a reset, no extra click on the cross
    if (!value && appliedValue.value && !props.loading) {
        submit('')
    }
})
</script>

<template>
    <form class="search-ui" @submit.prevent="applySearch" @keydown.escape.prevent="onEscape">
        <input
            v-model="search"
            class="search-ui__input ec_border"
            type="text"
            :inputmode="numericOnly ? 'numeric' : undefined"
            :maxlength="maxLength"
            :placeholder="$t('common.search')"
        >

        <ButtonLoading
            :loading="props.loading"
            :disabled="isButtonDisabled"
            class="search-ui__btn"
            type="submit"
        >
            <!-- both glyphs stay mounted so the swap can turn one into the other -->
            <span class="search-ui__icon" :class="{ 'is-clear': isClear }">
                <i class="bi bi-search search-ui__glyph --search"></i>
                <i class="bi bi-x-lg search-ui__glyph --clear"></i>
            </span>
        </ButtonLoading>
    </form>
</template>

<style scoped lang="scss">
.search-ui {
    display: flex;
    align-items: center;

    &__input {
        width: 255px;
        height: 50px;
        border: 1px solid lightgrey;
        color: black;
        padding-left: 10px;
        border-radius: 6px;
        padding-right: 50px;

        transition: border-color 0.2s, box-shadow 0.2s;

        &:focus {
            outline: none;
            border-color: var(--general-color);
            box-shadow: 0 0 0 3px #7a70df26;
        }
    }

    &__btn {
        width: 44px;
        height: 44px;
        background-color: var(--general-color);
        margin-left: -47px;
        border-radius: 6px;
        display: flex;
        justify-content: center;
        align-items: center;
    }

    &__icon {
        position: relative;
        display: block;
        width: 1em;
        height: 1em;
        font-size: 20px;
    }

    &__glyph {
        position: absolute;
        inset: 0;
        display: flex;
        align-items: center;
        justify-content: center;
        line-height: 1;
        transition: opacity 0.25s ease, transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);

        &::before {
            font-weight: 600 !important;
            -webkit-text-stroke: 0.35px currentColor;
        }

        // the lens turns away, the cross turns in with a slight overshoot
        &.--clear {
            opacity: 0;
            transform: rotate(-90deg) scale(0.5);
        }
    }

    &__icon.is-clear &__glyph {
        &.--search {
            opacity: 0;
            transform: rotate(90deg) scale(0.5);
        }

        &.--clear {
            opacity: 1;
            transform: none;
        }
    }

    @media (prefers-reduced-motion: reduce) {
        &__glyph {
            transition: none;
        }
    }

    @media (max-width: 740px) {
        .search-ui__input {
            font-size: 16px;
            height: auto;
            padding-bottom: 4px;
            padding-top: 4px;
            width: 195px;
        }

        .search-ui__btn {
            width: 30px;
            height: 30px;
            font-size: 15px;
            margin-left: -32px;
        }

        .search-ui__icon {
            font-size: 16px;
        }
    }
}
</style>
